// Staff's words for checkout options, and the small rules the screens share
// (words.test.js). Staff may see refusal protection's limits; customers
// never do (the website and the API's customer routes keep them out).

export const SCOPE_WORDS = { product: "this product", group: "the group", visa: "the country’s visa" };
export const FROM_WORDS = { product: "this product", group: "its group", visa: "its country’s visa" };

const cents = (n) => Math.round(Number(n || 0) * 100);

/** "$689", "$2.50", "€90", "£1,000". */
export function money(amount, currency = "USD") {
  if (amount === null || amount === undefined || amount === "") return "";
  const value = Number(amount);
  const whole = cents(value) % 100 === 0;
  try {
    return new Intl.NumberFormat(currency === "EUR" || currency === "GBP" ? "en-GB" : "en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

/** Where a setting comes from, in words: "Set on this product", "From its group (Schengen)". */
export function fromWords(from, own) {
  if (own) return "Set here";
  if (!from) return "Not set anywhere";
  return `From ${FROM_WORDS[from.scope] || from.scope}${from.name ? ` (${from.name})` : ""}`;
}

/** One protection quote for staff: "Offered: $21 per traveller", "Off for this visa (would cost $21 per traveller)." or "Not offered: …". */
export function quoteWords(quote) {
  if (!quote) return "";
  if (quote.offered) return `Offered: ${money(quote.price)} per traveller (covers ${money(quote.covered)}, ${quote.share}% of it).`;
  // Switched off: what it would cost once on, in place of the reason (sami-31).
  if (quote.reason === "off") return `Off for this visa${quote.would_price ? ` (would cost ${money(quote.would_price)} per traveller)` : ""}.`;
  return `Not offered: ${quote.reason_words || "not set up."}`;
}

/** The working behind a quote: "$25 + EUR 90 = $100.94 → $125.94 × 10.4% × 1.6 = $20.96, up to $21". */
export function workingWords(quote, limits) {
  if (!quote || quote.covered == null) return "";
  const loading = limits?.LOADING ?? 1.6;
  const raw = quote.rate != null ? Math.round(quote.covered * quote.rate * loading) / 100 : null;
  const parts = [`Covers ${quote.working} = ${money(quote.covered)}`];
  if (raw != null) parts.push(`× ${quote.rate}% × ${loading} = ${money(raw)}${quote.price != null ? `, rounded up to ${money(quote.price)}` : ""}`);
  return parts.join(" ");
}

/** The limits, for staff: "At least $5; not offered over 30% of what it covers, or when it covers $250 or more." */
export function limitsWords(limits) {
  if (!limits) return "";
  return `Price = what it covers × the refusal rate × ${limits.LOADING}, rounded up to the dollar, at least ${money(limits.MIN_PRICE)}. Not offered when that is over ${Math.round(limits.MAX_SHARE * 100)}% of what it covers, or when it covers ${money(limits.COVER_LIMIT)} or more.`;
}

/** A blank speed option for the form. */
export const blankSpeed = () => ({ name: "", fee_usd: "", official_amount: "", official_currency: "", decision_time: "", where_offered: "", source_url: "", source_label: "", checked_on: "" });

const blankText = (v) => v === null || v === undefined || String(v).trim() === "";

/**
 * What staff typed for the speed options, checked as the API checks it:
 * { errors: [{ index, field, text }], value: [...] }.
 */
export function speedsFromForm(rows) {
  const errors = [];
  const value = [];
  const seen = new Set();
  (rows || []).forEach((row, index) => {
    const err = (field, text) => errors.push({ index, field, text });
    const name = String(row.name || "").trim();
    if (!name) err("name", "Give it a name, such as Priority.");
    else if (/^standard$/i.test(name)) err("name", "“Standard” is always there: pick another name.");
    else if (seen.has(name.toLowerCase())) err("name", "Two options can’t share a name.");
    seen.add(name.toLowerCase());
    const fee = Number(row.fee_usd);
    if (blankText(row.fee_usd) || !(fee > 0)) err("fee_usd", "The extra fee in US dollars, more than 0.");
    const official = blankText(row.official_amount) ? null : Number(row.official_amount);
    if (official !== null && !(official > 0)) err("official_amount", "A number more than 0, or leave it empty.");
    const currency = String(row.official_currency || "").trim().toUpperCase();
    if (official !== null && !/^[A-Z]{3}$/.test(currency)) err("official_currency", "Its 3-letter currency, such as GBP.");
    if (blankText(row.decision_time)) err("decision_time", "How long the decision takes, as the official page says.");
    if (blankText(row.where_offered)) err("where_offered", "Where it’s offered, as the official page says.");
    if (!/^https:\/\/\S+$/.test(String(row.source_url || "").trim())) err("source_url", "The official page, an https link.");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(row.checked_on || "").trim())) err("checked_on", "The day you checked it, like 2026-10-30.");
    value.push({
      ...(row.key ? { key: row.key } : {}),
      name,
      fee_usd: fee,
      official_amount: official,
      official_currency: official !== null ? currency : null,
      decision_time: String(row.decision_time || "").trim(),
      where_offered: String(row.where_offered || "").trim(),
      source_url: String(row.source_url || "").trim(),
      source_label: String(row.source_label || "").trim() || null,
      checked_on: String(row.checked_on || "").trim(),
    });
  });
  return { errors, value };
}

/** The protection form's rate, checked: { error } or { value } (null to clear it). */
export function rateFromForm(form) {
  if (blankText(form.percent) && blankText(form.source_label) && blankText(form.source_url) && blankText(form.year)) return { value: null };
  const percent = Number(form.percent);
  if (!(percent > 0 && percent < 100)) return { error: { field: "percent", text: "A percent more than 0 and under 100, such as 10.4." } };
  if (blankText(form.source_label)) return { error: { field: "source_label", text: "The official source’s name." } };
  if (!/^https:\/\/\S+$/.test(String(form.source_url || "").trim())) return { error: { field: "source_url", text: "The official source, an https link." } };
  const year = Number(form.year);
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return { error: { field: "year", text: "The year the figure is for, such as 2025." } };
  return { value: { percent, source_label: String(form.source_label).trim(), source_url: String(form.source_url).trim(), year } };
}

/** The speed's state on a case, in words for staff. */
export function speedStateWords(speed) {
  if (!speed) return "";
  if (speed.status === "refund_due") return `Not offered at the centre: refund ${money(speed.total)} (${speed.travellers} × ${money(speed.fee)}).`;
  if (speed.status === "refunded") return `Not offered at the centre: ${money(speed.total)} refunded.`;
  if (speed.status === "chosen") return "Chosen on the draft; charged when it is submitted.";
  return "Our agent confirms the centre offers it before booking the appointment.";
}

export const PERCENT = (n) => `${n}%`;
